import type { SchemaCurrencyMatchInput } from 'hooks/customerApi';
import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { EntryVersionResponse } from '@fragment-compositions/visualizer/src/hooks/liveblocks';

import type { SafeString } from '@fragment/types';
import type { BaseSchemaLedgerEntryInputType } from '@fragment/types/schemas';

/**
 * Base interface for currency properties without path information
 */
export type CurrencyMatchInput = SchemaCurrencyMatchInput;

/**
 * Replaces spaces with underscores inside handlebars {{}} expressions.
 * This allows users to type spaces naturally and they will be converted to underscores.
 *
 * @example
 * replaceSpacesInHandlebars("{{my var}}") => "{{my_var}}"
 * replaceSpacesInHandlebars("100 + {{my amount}}") => "100 + {{my_amount}}"
 */
export const replaceSpacesInHandlebars = (value: string): string =>
  value.replace(/\{\{([^}]*?)\}\}/g, (match, content) => {
    const replacedContent = content.replace(/\s/g, '_');
    return `{{${replacedContent}}}`;
  });

/**
 * Replaces spaces with underscores inside handlebars {{}} expressions while preserving cursor position.
 * Since spaces and underscores are both 1 character, the cursor position remains the same.
 *
 * @param value - The input string
 * @param cursorPosition - The current cursor position
 * @returns An object with the transformed value and the cursor position (unchanged since space and _ are same length)
 *
 * @example
 * replaceSpacesInHandlebarsWithCursor("{{my var}}", 7) => { value: "{{my_var}}", cursorPosition: 7 }
 * replaceSpacesInHandlebarsWithCursor("100 + {{my amount}}", 15) => { value: "100 + {{my_amount}}", cursorPosition: 15 }
 */
export const replaceSpacesInHandlebarsWithCursor = (
  value: string,
  cursorPosition: number
): { value: string; cursorPosition: number } => {
  // Since spaces and underscores are both 1 character, cursor position stays the same
  const processedValue = replaceSpacesInHandlebars(value);
  return { value: processedValue, cursorPosition };
};

/**
 * Builds a currency object from an input string, splitting on the first colon
 * to handle both standard currencies and custom/parameterized currencies.
 *
 * @example
 * transformInputToCurrency("USD") // { code: "USD", customCurrencyId: undefined }
 * transformInputToCurrency("CUSTOM:my-token") // { code: "CUSTOM", customCurrencyId: "my-token" }
 * transformInputToCurrency("{{code}}:{{id}}") // { code: "{{code}}", customCurrencyId: "{{id}}" }
 */
export const transformInputToCurrency = (inputValue: string) => {
  const colonIndex = inputValue.indexOf(':');
  if (colonIndex > 0) {
    const currency: CurrencyMatchInput = {
      code: inputValue.substring(0, colonIndex) as SafeString,
      customCurrencyId: inputValue.substring(colonIndex + 1),
    };
    return currency;
  }
  const currency: CurrencyMatchInput = {
    code: inputValue as SafeString,
    customCurrencyId: undefined,
  };
  return currency;
};

export const transformCurrencyToInput = (currency: CurrencyMatchInput) =>
  currency.customCurrencyId
    ? `${currency.code}:${currency.customCurrencyId}`
    : currency.code;

/**
 * Formats a currency object for display in input fields.
 * Shows "CODE:customId" for custom currencies, or just "CODE" for standard ones.
 *
 * @example
 * formatCurrencyDisplay({ code: "USD" }) // "USD"
 * formatCurrencyDisplay({ code: "CUSTOM", customCurrencyId: "my-token" }) // "CUSTOM:my-token"
 * formatCurrencyDisplay(undefined) // ""
 */
export const formatCurrencyDisplay = (currency?: {
  code?: string;
  customCurrencyId?: string;
}) =>
  currency?.customCurrencyId
    ? `${currency.code}:${currency.customCurrencyId}`
    : currency?.code ?? '';

export const shouldShowBackwardsCompatibleWarning = ({
  isBackwardsCompatible,
  hasLedgers,
}: {
  isBackwardsCompatible: { isMatch: boolean };
  hasLedgers: boolean;
}): boolean => hasLedgers && !isBackwardsCompatible.isMatch;

/**
 * Custom hook that handles space-to-underscore replacement in handlebars while preserving cursor position.
 * Returns a ref for the input element and an onChange handler.
 *
 * @param value - The current value of the input
 * @param onValueChange - Callback that receives the processed value (with spaces replaced)
 * @returns An object with `inputRef` and `onChange` handler
 *
 * @example
 * const { inputRef, onChange } = useHandlebarsInputWithCursor(
 *   line.amount ?? '',
 *   (processedValue) => updateLine({ ...line, amount: processedValue })
 * );
 */
export const useHandlebarsInputWithCursor = (
  value: string,
  onValueChange: (processedValue: string) => void
) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingCursorPositionRef = useRef<number | null>(null);

  // Restore cursor position after value updates
  useEffect(() => {
    if (pendingCursorPositionRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(
        pendingCursorPositionRef.current,
        pendingCursorPositionRef.current
      );
      pendingCursorPositionRef.current = null;
    }
  }, [value]);

  const onChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const cursorPosition = evt.target.selectionStart ?? 0;
    const { value: processedValue, cursorPosition: newCursorPosition } =
      replaceSpacesInHandlebarsWithCursor(evt.target.value, cursorPosition);
    pendingCursorPositionRef.current = newCursorPosition;
    onValueChange(processedValue);
  };

  return { inputRef, onChange };
};

/**
 * Compares an EntryVersionResponse from Liveblocks with a deployed entry from the schema.
 * Returns an object indicating whether they match and which fields differ.
 *
 * Note: This function assumes that type and typeVersion have already been validated
 * to match between the two entries. Only compares the actual entry content fields.
 *
 * @param entryResponse - The entry from Liveblocks (EntryVersionResponse)
 * @param deployedEntry - The deployed entry from the schema (BaseSchemaLedgerEntryInputType)
 * @returns An object with `isMatch` boolean and `mismatchedFields` array
 *
 * @example
 * const comparison = compareEntryWithDeployed(entryResponse, deployedEntry);
 * if (!comparison.isMatch) {
 *   console.log('Fields differ:', comparison.mismatchedFields);
 * }
 */
const formatValue = (value: unknown): string => {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string') return `deployed value: "${value}"`;
  if (typeof value === 'object') {
    return `deployed value: ${JSON.stringify(value)}`;
  }
  return `deployed value: ${String(value)}`;
};

export const compareEntryWithDeployed = (
  entryResponse: EntryVersionResponse,
  deployedEntry: BaseSchemaLedgerEntryInputType
): { isMatch: boolean; mismatchedFields: string[] } => {
  const mismatchedFields: string[] = [];
  const entry = entryResponse.value;

  // Compare description
  const entryDescription = entry.description ?? undefined;
  const deployedDescription = deployedEntry.description ?? undefined;
  if (entryDescription !== deployedDescription) {
    mismatchedFields.push(`description: ${formatValue(deployedDescription)}`);
  }

  // Status is allowed to change to disable an active entry

  // Compare parameters - check each parameter individually
  const entryParams = entry.parameters
    ? Object.fromEntries(entry.parameters.map((p) => [p.key, p.value]))
    : {};
  const deployedParams = deployedEntry.parameters ?? {};
  const allParamKeys = new Set([
    ...Object.keys(entryParams),
    ...Object.keys(deployedParams),
  ]);
  allParamKeys.forEach((key) => {
    const entryValue = entryParams[key];
    const deployedValue = deployedParams[key];
    if (entryValue !== deployedValue) {
      mismatchedFields.push(
        `parameter[${key}] - ${formatValue(deployedValue)}`
      );
    }
  });

  // Compare tags - check each tag individually
  const entryTagsMap = new Map((entry.tags ?? []).map((t) => [t.key, t.value]));
  const deployedTagsMap = new Map(
    (deployedEntry.tags ?? []).map((t) => [t.key, t.value])
  );
  const allTagKeys = new Set([
    ...entryTagsMap.keys(),
    ...deployedTagsMap.keys(),
  ]);
  allTagKeys.forEach((key) => {
    const entryValue = entryTagsMap.get(key);
    const deployedValue = deployedTagsMap.get(key);
    if (entryValue !== deployedValue) {
      mismatchedFields.push(`tag[${key}] - ${formatValue(deployedValue)}`);
    }
  });

  // Compare groups - check each group individually
  const entryGroupsMap = new Map(
    (entry.groups ?? []).map((g) => [String(g.key), g.value])
  );
  const deployedGroupsMap = new Map(
    (deployedEntry.groups ?? []).map((g) => [String(g.key), g.value])
  );
  const allGroupKeys = new Set([
    ...entryGroupsMap.keys(),
    ...deployedGroupsMap.keys(),
  ]);
  allGroupKeys.forEach((key) => {
    const entryValue = entryGroupsMap.get(key);
    const deployedValue = deployedGroupsMap.get(key);
    if (entryValue !== deployedValue) {
      mismatchedFields.push(`group[${key}] - ${formatValue(deployedValue)}`);
    }
  });

  // Normalize currency object to handle undefined customCurrencyId consistently
  const normalizeCurrency = (currency?: {
    code: string;
    customCurrencyId?: string;
  }) => {
    if (!currency) return undefined;
    return {
      code: currency.code,
      ...(currency.customCurrencyId !== undefined
        ? { customCurrencyId: currency.customCurrencyId }
        : {}),
    };
  };

  // Compare conditions - check each condition and its sub-fields
  // Conditions are compared by index/position, not by account path
  const entryConditions = (entry.conditions ?? []).map((c) => ({
    account: c.account ? { path: c.account.path } : undefined,
    currency: normalizeCurrency(c.currency),
    precondition: c.precondition,
    postcondition: c.postcondition,
    repeated: c.repeated ? { key: String(c.repeated.key) } : undefined,
  }));
  const deployedConditions = (deployedEntry.conditions ?? []).map((c) => ({
    account: c.account ? { path: c.account.path } : undefined,
    currency: normalizeCurrency(c.currency),
    precondition: c.precondition,
    postcondition: c.postcondition,
    repeated: c.repeated ? { key: String(c.repeated.key) } : undefined,
  }));
  const maxConditionsLength = Math.max(
    entryConditions.length,
    deployedConditions.length
  );
  Array.from({ length: maxConditionsLength }).forEach((_, index) => {
    const entryCondition = entryConditions.at(index);
    const deployedCondition = deployedConditions.at(index);
    if (!entryCondition && deployedCondition) {
      mismatchedFields.push(
        `condition[${index}] - missing (deployed: ${formatValue(
          deployedCondition
        )})`
      );
      return;
    }
    if (entryCondition && !deployedCondition) {
      mismatchedFields.push(
        `condition[${index}] - does not exist in deployed entry`
      );
      return;
    }
    if (!entryCondition || !deployedCondition) {
      return;
    }
    if (entryCondition.account?.path !== deployedCondition.account?.path) {
      mismatchedFields.push(
        `condition[${index}].account - ${formatValue(
          deployedCondition.account?.path
        )}`
      );
    }
    if (
      JSON.stringify(entryCondition.currency) !==
      JSON.stringify(deployedCondition.currency)
    ) {
      mismatchedFields.push(
        `condition[${index}].currency - ${formatValue(
          deployedCondition.currency
        )}`
      );
    }
    if (
      JSON.stringify(entryCondition.precondition) !==
      JSON.stringify(deployedCondition.precondition)
    ) {
      mismatchedFields.push(
        `condition[${index}].precondition - ${formatValue(
          deployedCondition.precondition
        )}`
      );
    }
    if (
      JSON.stringify(entryCondition.postcondition) !==
      JSON.stringify(deployedCondition.postcondition)
    ) {
      mismatchedFields.push(
        `condition[${index}].postcondition - ${formatValue(
          deployedCondition.postcondition
        )}`
      );
    }
    if (
      JSON.stringify(entryCondition.repeated) !==
      JSON.stringify(deployedCondition.repeated)
    ) {
      mismatchedFields.push(
        `condition[${index}].repeated - ${formatValue(
          deployedCondition.repeated
        )}`
      );
    }
  });

  // postLinesAs is intentionally excluded — it can be changed on deployed entries
  // without backwards compatibility concerns.

  // Compare lines - check each line and its sub-fields
  const entryLinesMap = new Map(
    (entry.lines ?? []).map((l) => [
      l.key,
      {
        key: l.key,
        account: l.account ? { path: l.account.path } : undefined,
        amount: l.amount ?? undefined,
        currency: normalizeCurrency(l.currency),
        description: l.description ?? undefined,
        tx: l.tx,
        repeated: l.repeated ? { key: String(l.repeated.key) } : undefined,
      },
    ])
  );
  const deployedLinesMap = new Map(
    (deployedEntry.lines ?? []).map((l) => [
      l.key,
      {
        key: l.key,
        account: l.account ? { path: l.account.path } : undefined,
        amount: l.amount ?? undefined,
        currency: normalizeCurrency(l.currency),
        description: l.description ?? undefined,
        tx: l.tx,
        repeated: l.repeated ? { key: String(l.repeated.key) } : undefined,
      },
    ])
  );
  const allLineKeys = new Set([
    ...entryLinesMap.keys(),
    ...deployedLinesMap.keys(),
  ]);
  Array.from(allLineKeys).forEach((key, index) => {
    const entryLine = entryLinesMap.get(key);
    const deployedLine = deployedLinesMap.get(key);
    if (!entryLine || !deployedLine) {
      mismatchedFields.push(
        `line - missing (deployed: ${formatValue(deployedLine)})`
      );
      return;
    }
    // Compare sub-fields
    if (entryLine.account?.path !== deployedLine.account?.path) {
      mismatchedFields.push(
        `line[${index}].account - ${formatValue(deployedLine.account?.path)}`
      );
    }
    if (entryLine.amount !== deployedLine.amount) {
      mismatchedFields.push(
        `line[${index}].amount - ${formatValue(deployedLine.amount)}`
      );
    }
    if (
      JSON.stringify(entryLine.currency) !==
      JSON.stringify(deployedLine.currency)
    ) {
      mismatchedFields.push(
        `line[${index}].currency - ${formatValue(deployedLine.currency)}`
      );
    }
    if (entryLine.description !== deployedLine.description) {
      mismatchedFields.push(
        `line[${index}].description - ${formatValue(deployedLine.description)}`
      );
    }
    if (JSON.stringify(entryLine.tx) !== JSON.stringify(deployedLine.tx)) {
      mismatchedFields.push(
        `line[${index}].tx - ${formatValue(deployedLine.tx)}`
      );
    }
    if (
      JSON.stringify(entryLine.repeated) !==
      JSON.stringify(deployedLine.repeated)
    ) {
      mismatchedFields.push(
        `line[${index}].repeated - ${formatValue(deployedLine.repeated)}`
      );
    }
  });

  return {
    isMatch: mismatchedFields.length === 0,
    mismatchedFields,
  };
};

/**
 * Generic hook for managing a `Set<string>` of expanded/toggled IDs.
 */
export const useToggleSet = () => {
  const [ids, setIds] = useState<Set<string>>(() => new Set());

  const toggle = (id: string) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return [ids, toggle] as const;
};
